/* @flow */
import * as React from 'react';
import ReactDOM from 'react-dom';
import Input from 'components/Input.jsx';
import invariant from 'invariant';

import getAutocompleteScript from 'lib/GooglePlacesAutocomplete';
import createChainedFunction from 'utils/createChainedFunction';

type Place = {
  latitude: string,
  longitude: string,
  streetAddress: string,
  city: string,
  state: string,
  country: string,
  postalCode: string,
};

type State = {
  value: ?string,
};

type Props = {
  onBlur?: () => void,
  onChange?: (address?: Place) => void,
  value?: ?Object,
  className?: string,
  placeholder?: string,
};

function getPart(place, type: string) {
  const component = place.address_components.find(c => c.types.includes(type));
  return component && component.long_name;
}

class GooglePlacesAutocompleteInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { value } = props;
    this.state = {
      value: value
        ? `${value.streetAddress}, ${value.city}, ${value.state} ${value.postalCode}`
        : null,
    };
  }

  componentDidMount() {
    getAutocompleteScript()
      .then(Autocomplete => {
        const el = ReactDOM.findDOMNode(this._input);
        this.autocomplete = new Autocomplete(el, {
          componentRestrictions: { country: ['ca', 'us'] },
          types: ['address'],
        });
        this.autocomplete.addListener('place_changed', this.handlePlaceChanged);
      })
      .catch(e => {
        console.warn(e, e && e.stack);
        console.error('getAutocompleteScript error');
      });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.value !== prevProps.value) {
      this.setAddress(this.props.value);
    }
  }

  setAddress = (address: ?Object) => {
    this.setState({
      value: address
        ? `${address.streetAddress}, ${address.city}, ${address.state} ${address.postalCode}`
        : '',
    });
  };

  _input = null;
  autocomplete = null;

  handlePlaceChanged = () => {
    invariant(this.autocomplete, 'handlePlaceChanged called with no autocomplete');
    const { onChange } = this.props;
    if (onChange) {
      const place = this.autocomplete.getPlace();
      if (place && place.address_components) {
        const streetNumber = getPart(place, 'street_number');
        const streetName = getPart(place, 'route');
        const postalCode = getPart(place, 'postal_code');
        const address = {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          streetAddress: streetNumber && streetName ? `${streetNumber} ${streetName}` : '',
          city:
            getPart(place, 'locality') ||
            getPart(place, 'sublocality') ||
            getPart(place, 'neighborhood'),
          state: getPart(place, 'administrative_area_level_1'),
          country: getPart(place, 'country'),
          postalCode: postalCode || '',
        };
        onChange(address);
        this.setAddress(address);
      } else {
        onChange();
      }
    }
  };

  handleChange = (event: *) => {
    const { value } = event.target;
    this.setState({
      value,
    });
    // If the input is empty, clear the address. Otherwise the address is set in handlePlaceChanged
    if (!value && this.props.onChange) {
      this.props.onChange();
    }
  };

  render() {
    const { onChange, onBlur, placeholder, ...rest } = this.props; // eslint-disable-line no-unused-vars
    return (
      <Input
        {...rest}
        type="text"
        ref={r => {
          this._input = r;
        }}
        value={this.state.value}
        onChange={this.handleChange}
        onBlur={createChainedFunction(this.handlePlaceChanged, onBlur)}
        placeholder={placeholder}
      />
    );
  }
}

export { GooglePlacesAutocompleteInput };
export default GooglePlacesAutocompleteInput;
